import { parse as parseFont, type Font, type VariationCoords } from 'opentype.js';
import { FONT_BY_ID, fontUrl, type CatalogFont } from './catalog';
import { cacheGet, cachePut } from './fontCache';

export interface VariableAxis {
  tag: string;
  name: string;
  min: number;
  max: number;
  default: number;
}

export interface LoadedFont {
  id: string;
  family: string;
  /** Parsed outline source — the single source of truth for both mask and vectors. */
  font: Font;
  unitsPerEm: number;
  ascender: number;
  descender: number;
  axes: VariableAxis[];
  /** True if the font exposes a `wght` axis, i.e. the weight slider does anything. */
  hasWeightAxis: boolean;
  source: 'catalog' | 'upload';
  /** Original bytes, kept so uploaded fonts can be re-cached and embedded in exports. */
  buffer: ArrayBuffer;
}

const loaded = new Map<string, LoadedFont>();
const inflight = new Map<string, Promise<LoadedFont>>();

/** Prefix marking fonts that came from the user's disk rather than the catalogue. */
export const UPLOAD_PREFIX = 'upload:';

function readAxisName(name: Record<string, string> | string, fallback: string): string {
  if (typeof name === 'string') return name;
  return name.en ?? Object.values(name)[0] ?? fallback;
}

function describe(font: Font, id: string, family: string, source: LoadedFont['source'], buffer: ArrayBuffer): LoadedFont {
  const fvar = font.tables.fvar;
  const axes: VariableAxis[] = (fvar?.axes ?? []).map((a) => ({
    tag: a.tag,
    name: readAxisName(a.name, a.tag),
    min: a.minValue,
    max: a.maxValue,
    default: a.defaultValue,
  }));
  return {
    id,
    family,
    font,
    unitsPerEm: font.unitsPerEm,
    ascender: font.ascender,
    descender: font.descender,
    axes,
    hasWeightAxis: axes.some((a) => a.tag === 'wght'),
    source,
    buffer,
  };
}

/**
 * Registers the binary as a CSS FontFace so the picker can preview it with real type.
 * Purely cosmetic — the canvas never uses it.
 */
function registerCssFace(family: string, buffer: ArrayBuffer): void {
  if (typeof document === 'undefined' || typeof FontFace === 'undefined') return;
  try {
    const face = new FontFace(family, buffer);
    void face
      .load()
      .then((f) => document.fonts.add(f))
      .catch(() => undefined);
  } catch {
    /* preview only — never fatal */
  }
}

export function getLoadedFont(id: string): LoadedFont | undefined {
  return loaded.get(id);
}

export async function loadCatalogFont(id: string): Promise<LoadedFont> {
  const existing = loaded.get(id);
  if (existing) return existing;
  const pending = inflight.get(id);
  if (pending) return pending;

  const entry: CatalogFont | undefined = FONT_BY_ID.get(id);
  if (!entry) throw new Error(`Unbekannte Font-ID: ${id}`);

  const task = (async (): Promise<LoadedFont> => {
    let buffer = await cacheGet(id);
    if (!buffer) {
      const res = await fetch(fontUrl(entry));
      if (!res.ok) throw new Error(`Font "${entry.family}" konnte nicht geladen werden (HTTP ${res.status})`);
      buffer = await res.arrayBuffer();
      void cachePut(id, buffer);
    }
    const font = parseFont(buffer);
    const result = describe(font, id, entry.family, 'catalog', buffer);
    loaded.set(id, result);
    registerCssFace(entry.family, buffer.slice(0));
    return result;
  })();

  inflight.set(id, task);
  try {
    return await task;
  } finally {
    inflight.delete(id);
  }
}

/** Restores an uploaded font from the IndexedDB cache (used when a preset references it). */
export async function restoreUploadedFont(id: string): Promise<LoadedFont | null> {
  const existing = loaded.get(id);
  if (existing) return existing;
  const buffer = await cacheGet(id);
  if (!buffer) return null;
  try {
    const font = parseFont(buffer);
    const family = id.slice(UPLOAD_PREFIX.length);
    const result = describe(font, id, family, 'upload', buffer);
    loaded.set(id, result);
    registerCssFace(family, buffer.slice(0));
    return result;
  } catch {
    return null;
  }
}

const WOFF2_HINT =
  'WOFF2 wird von opentype.js nicht direkt unterstützt. Bitte lade die TTF- oder OTF-Variante hoch ' +
  '(z. B. mit fontTools: `fonttools ttLib.woff2 decompress datei.woff2`).';

export async function loadUploadedFont(file: File): Promise<LoadedFont> {
  const buffer = await file.arrayBuffer();
  const sig = new Uint8Array(buffer, 0, Math.min(4, buffer.byteLength));
  if (String.fromCharCode(...sig) === 'wOF2') throw new Error(WOFF2_HINT);

  let font: Font;
  try {
    font = parseFont(buffer);
  } catch (err) {
    throw new Error(`Font konnte nicht geparst werden: ${(err as Error).message}`);
  }

  const nameFromFile = file.name.replace(/\.(ttf|otf|woff2?)$/i, '');
  const family = font.names.fontFamily?.en ?? Object.values(font.names.fontFamily ?? {})[0] ?? nameFromFile;
  const id = `${UPLOAD_PREFIX}${family}`;

  const result = describe(font, id, family, 'upload', buffer);
  loaded.set(id, result);
  void cachePut(id, buffer.slice(0));
  registerCssFace(family, buffer.slice(0));
  return result;
}

export function listUploadedFonts(): LoadedFont[] {
  return [...loaded.values()].filter((f) => f.source === 'upload');
}

/** Clamps user-supplied axis values to what the font actually supports. */
export function sanitiseVariation(font: LoadedFont, coords: VariationCoords): VariationCoords {
  const out: VariationCoords = {};
  for (const axis of font.axes) {
    const v = coords[axis.tag];
    out[axis.tag] = v === undefined ? axis.default : Math.min(axis.max, Math.max(axis.min, v));
  }
  return out;
}
